import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useDashboardsApi } from '@app/hooks/useApis';
import { useGrafanaEmbedSessionKeepalive } from '@app/hooks/useGrafanaEmbedSessionKeepalive';
import Tabs from '@app/components/Tabs';
import KpiCard from '@app/components/KpiCard';
import ShortcutButton from '@app/components/ShortcutButton';
import DataTable from '@app/components/DataTable';
import GrafanaIframeCard from '@app/components/GrafanaIframeCard';
import SignalsSummary from '@app/components/SignalsSummary';
import { ChevronLeftIcon, ChevronRightIcon } from '@app/components/icons';
import { formatDateTime, formatNumber } from '@app/utils/format';
import { buildAuditNameLookups, getAuditActorLabel, getAuditResourceLabel, getAuditEventLabel, normalizeAuditEvent } from '@app/utils/audit';
import { useConfig } from '@config/provider';
import { resolveBasePath } from '@app/utils/api';
import { computeTimeRange, type TimeRangePreset } from '@app/utils/time';
import type {
  ApiKeySummary,
  AuditEventSummary,
  ChatThreadSummary,
  ConnectorSummary,
  CursorListResponse,
  DeviceSummary,
  IdpConnectionSummary,
  InvitationSummary,
  MembershipSummary,
  RoleSummary,
  SiteSummary
} from '@app/types/org-console';
import type { DashboardWidgetSpec, DashboardsRecommendedResponse } from '@app/types/sdk';

interface OverviewMetrics {
  members: number;
  roles: number;
  pendingInvites: number;
  ssoConnections: number;
  ssoOidc: number;
  ssoSaml: number;
  sites: number;
  connectors: number;
  devices: number;
  chatThreads: number;
  apiKeys: number;
}

const AUDIT_PAGE_SIZES = [5, 10, 25];
const DEFAULT_AUDIT_PAGE_SIZE = 10;
const COCKPIT_RANGE_PRESET: TimeRangePreset = '1h';

type OverviewTab = 'cockpit' | 'admin';

const COCKPIT_REFRESH_OPTIONS_SECONDS = [0, 5, 10, 30, 60] as const;

const OrgOverviewPage = () => {
  const orgId = useActiveOrgId();
  const { apiBaseUrl } = useConfig();
  const navigate = useNavigate();
  const basePath = resolveBasePath(apiBaseUrl);
  const { fetchOrgCollection } = useAuthFetch();
  const dashboardsApi = useDashboardsApi();

  const [activeTab, setActiveTab] = useState<OverviewTab>('cockpit');
  const [cockpitRefreshSeconds, setCockpitRefreshSeconds] = useState<number>(30);
  const refreshGrafanaEmbedSession = useCallback(() => {
    if (!orgId) {
      return Promise.resolve();
    }
    return dashboardsApi.orgsOrgIdGrafanaEmbedSessionPost({ orgId }, { credentials: 'include' }).then(() => undefined);
  }, [dashboardsApi, orgId]);

  const metricsFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    if (activeTab !== 'admin') {
      return null;
    }

    return async (): Promise<OverviewMetrics> => {
      const fetchOrEmpty = async <T,>(path: string): Promise<CursorListResponse<T>> => {
        try {
          return await fetchOrgCollection<T>(basePath, orgId, path);
        } catch (error) {
          console.warn(`Failed to load ${path} while building overview metrics`, error);
          return { items: [] } as CursorListResponse<T>;
        }
      };

      const getCollectionCount = <T,>(response: CursorListResponse<T>): number => response.meta?.exactTotal ?? response.items?.length ?? 0;

      const [
        memberships,
        roles,
        connections,
        oidcConnections,
        samlConnections,
        pendingInvitations,
        sites,
        connectors,
        devices,
        threads,
        apiKeys
      ] = await Promise.all([
        fetchOrEmpty<MembershipSummary>('memberships?limit=1'),
        fetchOrEmpty<RoleSummary>('roles?limit=1'),
        fetchOrEmpty<IdpConnectionSummary>('idp/connections?limit=1'),
        fetchOrEmpty<IdpConnectionSummary>('idp/connections?limit=1&type=oidc'),
        fetchOrEmpty<IdpConnectionSummary>('idp/connections?limit=1&type=saml'),
        fetchOrEmpty<InvitationSummary>('invitations?limit=1&status=pending'),
        fetchOrEmpty<SiteSummary>('sites?limit=1'),
        fetchOrEmpty<ConnectorSummary>('connectors?limit=1'),
        fetchOrEmpty<DeviceSummary>('devices?limit=1'),
        fetchOrEmpty<ChatThreadSummary>('chat/threads?limit=1'),
        fetchOrEmpty<ApiKeySummary>('api-keys?limit=1')
      ]);

      return {
        members: getCollectionCount(memberships),
        roles: getCollectionCount(roles),
        pendingInvites: getCollectionCount(pendingInvitations),
        ssoConnections: getCollectionCount(connections),
        ssoOidc: getCollectionCount(oidcConnections),
        ssoSaml: getCollectionCount(samlConnections),
        sites: getCollectionCount(sites),
        connectors: getCollectionCount(connectors),
        devices: getCollectionCount(devices),
        chatThreads: getCollectionCount(threads),
        apiKeys: getCollectionCount(apiKeys)
      };
    };
  }, [activeTab, basePath, orgId]);

  const {
    data: metrics,
    loading: metricsLoading,
    error: metricsError,
    backendMissing: metricsBackendMissing
  } = useApiQuery<OverviewMetrics>(metricsFetcher, [metricsFetcher]);

  const auditFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    if (activeTab !== 'admin') {
      return null;
    }
    return () => fetchOrgCollection<AuditEventSummary>(basePath, orgId, 'audit/events?limit=50');
  }, [activeTab, basePath, orgId]);

  const {
    data: audit,
    loading: auditLoading,
    error: auditError,
    backendMissing: auditBackendMissing
  } = useApiQuery<CursorListResponse<AuditEventSummary>>(auditFetcher, [auditFetcher]);

  const membershipFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    if (activeTab !== 'admin') {
      return null;
    }
    return () => fetchOrgCollection<MembershipSummary>(basePath, orgId, 'memberships?limit=500');
  }, [activeTab, basePath, fetchOrgCollection, orgId]);

  const { data: membershipDirectory } = useApiQuery<CursorListResponse<MembershipSummary>>(membershipFetcher, [membershipFetcher]);
  const auditNameLookups = useMemo(() => buildAuditNameLookups(membershipDirectory?.items ?? []), [membershipDirectory]);

  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(DEFAULT_AUDIT_PAGE_SIZE);
  const auditRows = useMemo(() => (audit?.items ?? []).map(normalizeAuditEvent), [audit]);
  const auditTotalCount = auditRows.length;
  const auditPageCount = Math.max(1, Math.ceil(auditTotalCount / auditPageSize));
  const safeAuditPage = Math.min(auditPage, auditPageCount);
  const auditStartIndex = auditTotalCount === 0 ? 0 : (safeAuditPage - 1) * auditPageSize + 1;
  const auditEndIndex = auditTotalCount === 0 ? 0 : Math.min(auditTotalCount, safeAuditPage * auditPageSize);
  const pagedAuditRows = useMemo(() => {
    const start = (safeAuditPage - 1) * auditPageSize;
    return auditRows.slice(start, start + auditPageSize);
  }, [auditRows, auditPageSize, safeAuditPage]);

  useEffect(() => {
    setAuditPage(1);
  }, [orgId]);

  useEffect(() => {
    if (auditPage > auditPageCount) {
      setAuditPage(auditPageCount);
    }
  }, [auditPage, auditPageCount]);

  if (!orgId) {
    return (
      <OrgModuleLayout
        title="Overview"
        description="Status snapshot, KPIs, and recent activity for the selected organization."
        scrollMode="all"
        topSlot={<OrgModulePlaceholder intent="Pick an organization to load overview metrics." />}
      >
        <OrgModulePlaceholder intent="Activity feed, device deltas, and reminders show here once an organization is active." />
      </OrgModuleLayout>
    );
  }

  const cockpitTimeRange = useMemo(() => computeTimeRange(COCKPIT_RANGE_PRESET), []);

  useGrafanaEmbedSessionKeepalive({
    dashboardsApi,
    orgId,
    enabled: activeTab === 'cockpit',
    logTag: 'overview'
  });

  const cockpitFetcher = useMemo(() => {
    if (activeTab !== 'cockpit') {
      return null;
    }
    return (): Promise<DashboardsRecommendedResponse> =>
      dashboardsApi.orgsOrgIdDashboardsRecommendedGet({
        orgId,
        pageKey: 'org.cockpit',
        scopeType: 'org',
        scopeId: orgId,
        from: cockpitTimeRange.from,
        to: cockpitTimeRange.to
      });
  }, [activeTab, cockpitTimeRange.from, cockpitTimeRange.to, dashboardsApi, orgId]);

  const {
    data: cockpit,
    loading: cockpitLoading,
    error: cockpitError,
    backendMissing: cockpitBackendMissing
  } = useApiQuery<DashboardsRecommendedResponse>(cockpitFetcher, [cockpitFetcher]);

  const cockpitWidgets = useMemo(() => {
    const widgets = cockpit?.widgets ?? [];
    return widgets.filter((w: DashboardWidgetSpec) => w.type === 'grafana_panel_iframe') as DashboardWidgetSpec[];
  }, [cockpit]);

  const kpiValue = (value: number) => (metricsLoading ? '…' : formatNumber(value));
  const showMetricsError = metricsError && !metricsBackendMissing;
  const metricsUnavailable = metricsBackendMissing ? (
    <OrgModulePlaceholder intent="Implement this API in the backend to populate overview metrics." />
  ) : null;

  const tabs = useMemo(
    () => [
      { id: 'cockpit', label: 'Cockpit' },
      { id: 'admin', label: 'Admin' }
    ],
    []
  );

  const cockpitPanel = cockpitBackendMissing ? (
    <OrgModulePlaceholder intent="Implement dashboards-service + api-server wiring to populate cockpit recommendations." />
  ) : (
    <div className="org-cockpit__layout">
      <div className="org-cockpit__toolbar">
        <label>
          Refresh
          <select value={cockpitRefreshSeconds} onChange={(e) => setCockpitRefreshSeconds(parseInt(e.target.value, 10) || 0)}>
            {COCKPIT_REFRESH_OPTIONS_SECONDS.map((seconds) => (
              <option key={seconds} value={seconds}>
                {seconds === 0 ? 'Off' : `${seconds}s`}
              </option>
            ))}
          </select>
        </label>
      </div>

      {cockpitError && <p className="org-shell__banner">{cockpitError.message}</p>}
      {cockpitLoading && <p className="org-shell__banner">Loading cockpit…</p>}
      <SignalsSummary scopeType="org" scopeId={orgId} refreshSeconds={cockpitRefreshSeconds} />

      {cockpitWidgets.length > 0 ? (
        <div className="org-cockpit__grid">
          {cockpitWidgets.map((widget) => (
            <GrafanaIframeCard
              key={widget.id}
              widget={widget}
              refreshSeconds={cockpitRefreshSeconds}
              timeRangePreset={COCKPIT_RANGE_PRESET}
              onAuthRefresh={refreshGrafanaEmbedSession}
            />
          ))}
        </div>
      ) : null}
    </div>
  );

  const adminPanel = metricsUnavailable ?? (
    <div className="overview-top">
      <div className="overview-top__layout">
        <div className="overview-top__metrics kpi-grid">
          <KpiCard label="Members" value={kpiValue(metrics?.members ?? 0)} secondary={`Roles: ${kpiValue(metrics?.roles ?? 0)}`} />
          <KpiCard label="Pending Invites" value={kpiValue(metrics?.pendingInvites ?? 0)} />
          <KpiCard
            label="SSO Connections"
            value={kpiValue(metrics?.ssoConnections ?? 0)}
            secondary={`OIDC ${kpiValue(metrics?.ssoOidc ?? 0)} • SAML ${kpiValue(metrics?.ssoSaml ?? 0)}`}
          />
          <KpiCard label="Sites" value={kpiValue(metrics?.sites ?? 0)} />
          <KpiCard label="Connectors" value={kpiValue(metrics?.connectors ?? 0)} />
          <KpiCard label="Devices" value={kpiValue(metrics?.devices ?? 0)} />
          <KpiCard label="Chat Threads" value={kpiValue(metrics?.chatThreads ?? 0)} />
          <KpiCard label="API Keys" value={kpiValue(metrics?.apiKeys ?? 0)} />
        </div>
        <div className="overview-top__actions shortcut-bar">
          <ShortcutButton label="Invite Member" onClick={() => navigate('/app/people-access?intent=invite')} />
          <ShortcutButton label="Add IdP" onClick={() => navigate('/app/people-access?intent=new-oidc-connection')} />
          <ShortcutButton label="New Site" onClick={() => navigate('/app/connectivity?intent=new-site')} />
          <ShortcutButton label="Generate API Key" onClick={() => navigate('/app/people-access?intent=new-api-key')} />
        </div>
      </div>
      {showMetricsError && (
        <p className="org-shell__banner" role="alert">
          {metricsError.message}
        </p>
      )}
    </div>
  );

  const topSlot = (
    <div>
      <Tabs items={tabs} activeId={activeTab} onChange={(id) => setActiveTab(id as OverviewTab)} />
      <div className="ui-tabs__panel">{activeTab === 'cockpit' ? cockpitPanel : adminPanel}</div>
    </div>
  );

  const renderAuditTable = () => {
    if (auditBackendMissing) {
      return (
        <OrgModulePlaceholder intent="Implement this API in the backend to populate audit history." />
      );
    }

    return (
      <>
        <h2 className="overview-section__title">Audit Records</h2>
        <div className="overview-audit">
          <DataTable
            columns={[
              { key: 'time', header: 'When', render: (row) => formatDateTime(row.createdAt) },
              {
                key: 'actor',
                header: 'Actor',
                render: (row) => getAuditActorLabel(row.actor, { lookups: auditNameLookups, metadata: row.metadata })
              },
              {
                key: 'event',
                header: 'Event',
                render: (row) => getAuditEventLabel(row)
              },
              {
                key: 'resource',
                header: 'Resource',
                render: (row) => getAuditResourceLabel(row, auditNameLookups)
              }
            ]}
            rows={pagedAuditRows}
            rowId={(row, index) => row.id ?? `${row.type ?? 'event'}-${(safeAuditPage - 1) * auditPageSize + index}`}
            emptyState={auditLoading ? <p>Loading audit events…</p> : <p>No recent activity yet.</p>}
          />
          {auditTotalCount > 0 && (
            <div className="overview-audit__footer">
              <div className="overview-audit__range">
                {`${auditStartIndex}-${auditEndIndex} of ${auditTotalCount} entries`}
              </div>
              <div className="overview-audit__pager">
                <label className="overview-audit__page-size">
                  Rows per page
                  <select
                    value={auditPageSize}
                    onChange={(event) => {
                      const next = parseInt(event.target.value, 10) || DEFAULT_AUDIT_PAGE_SIZE;
                      setAuditPageSize(next);
                      setAuditPage(1);
                    }}
                  >
                    {AUDIT_PAGE_SIZES.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="overview-audit__pagination">
                  <button
                    type="button"
                    className="list-workspace__icon-button"
                    onClick={() => setAuditPage(Math.max(1, safeAuditPage - 1))}
                    disabled={safeAuditPage <= 1}
                    aria-label="Previous page"
                  >
                    <ChevronLeftIcon size={18} />
                  </button>
                  <span>
                    Page {safeAuditPage} of {auditPageCount}
                  </span>
                  <button
                    type="button"
                    className="list-workspace__icon-button"
                    onClick={() => setAuditPage(Math.min(auditPageCount, safeAuditPage + 1))}
                    disabled={safeAuditPage >= auditPageCount}
                    aria-label="Next page"
                  >
                    <ChevronRightIcon size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <OrgModuleLayout
      title="Overview"
      description="Status snapshot, KPIs, and recent activity for the selected organization."
      scrollMode="all"
      topSlot={topSlot}
    >
      {activeTab === 'admin'
        ? auditError && !auditBackendMissing
          ? <OrgModulePlaceholder intent={`Failed to load recent activity: ${auditError.message}`} />
          : renderAuditTable()
        : null}
    </OrgModuleLayout>
  );
};

export default OrgOverviewPage;
